import { Card, CardContent, CardHeader, Box, Typography } from "@mui/material";
import {
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { formatValue, type BaseChartConfig, type Widget } from "./types";

const AXIS_COLOR = "#374151";
const GRID_COLOR = "rgba(0,0,0,0.08)";
const TOOLTIP_BG = "rgba(15, 23, 42, 0.92)";
const TOOLTIP_TEXT = "#f9fafb";

interface ScatterConfig extends BaseChartConfig {
  /** Field name carrying X numeric value on each point. Default: 'x'. */
  x_key?: string;
  /** Field name carrying Y numeric value. Default: 'y'. */
  y_key?: string;
  /** Optional bubble-size field — turns scatter into a bubble chart. */
  size_key?: string;
  /** Reference lines that partition the plane into quadrants (menu-engineering
   *  matrix style). e.g. {x_line: average_volume, y_line: average_margin}. */
  x_line?: number;
  y_line?: number;
  /** Format hint for the Y axis (and tooltip). x_format for X. */
  x_format?: BaseChartConfig["format"];
}

export function ScatterCard({ widget }: { widget: Widget }) {
  const cfg = widget.config as ScatterConfig;
  const rawPoints = widget.data?.points ?? [];
  const xKey = cfg.x_key ?? "x";
  const yKey = cfg.y_key ?? "y";
  const points = rawPoints.filter(
    (p): p is Record<string, unknown> =>
      p !== null &&
      typeof p === "object" &&
      typeof (p as Record<string, unknown>)[xKey] === "number" &&
      typeof (p as Record<string, unknown>)[yKey] === "number",
  );

  return (
    <Card variant="outlined">
      {widget.title && (
        <CardHeader
          title={widget.title}
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 700, letterSpacing: "-0.01em" }}
          subheaderTypographyProps={{ variant: "caption", color: "text.secondary" }}
          sx={{ pb: 1 }}
        />
      )}
      <CardContent sx={{ pt: 0 }}>
        {points.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {widget.data?.error ? `No data (${widget.data.error})` : "No data."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%", height: { xs: 320, sm: 380, md: 420 } }}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 16, right: 24, left: 24, bottom: 32 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                <XAxis
                  type="number"
                  dataKey={xKey}
                  name={cfg.x_label ?? xKey}
                  tick={{ fill: AXIS_COLOR, fontSize: 11 }}
                  stroke={AXIS_COLOR}
                  tickFormatter={(v: number) => formatValue(v, cfg.x_format ?? cfg.format)}
                  label={
                    cfg.x_label
                      ? {
                          value: cfg.x_label,
                          position: "insideBottom",
                          offset: -8,
                          fill: AXIS_COLOR,
                        }
                      : undefined
                  }
                />
                <YAxis
                  type="number"
                  dataKey={yKey}
                  name={cfg.y_label ?? yKey}
                  tick={{ fill: AXIS_COLOR, fontSize: 11 }}
                  stroke={AXIS_COLOR}
                  tickFormatter={(v: number) => formatValue(v, cfg.format)}
                  label={
                    cfg.y_label
                      ? {
                          value: cfg.y_label,
                          angle: -90,
                          position: "insideLeft",
                          fill: AXIS_COLOR,
                        }
                      : undefined
                  }
                />
                {cfg.size_key && (
                  <ZAxis dataKey={cfg.size_key} range={[40, 400]} />
                )}
                {cfg.x_line !== undefined && (
                  <ReferenceLine
                    x={cfg.x_line}
                    stroke="#9ca3af"
                    strokeDasharray="4 4"
                    strokeWidth={1.2}
                  />
                )}
                {cfg.y_line !== undefined && (
                  <ReferenceLine
                    y={cfg.y_line}
                    stroke="#9ca3af"
                    strokeDasharray="4 4"
                    strokeWidth={1.2}
                  />
                )}
                <Tooltip
                  cursor={{ strokeDasharray: "3 3" }}
                  contentStyle={{
                    backgroundColor: TOOLTIP_BG,
                    border: "none",
                    borderRadius: 6,
                    color: TOOLTIP_TEXT,
                  }}
                  labelStyle={{ color: TOOLTIP_TEXT, fontWeight: 600 }}
                  itemStyle={{ color: TOOLTIP_TEXT }}
                  formatter={(v: number, n: string) => {
                    const fmt = n === (cfg.x_label ?? xKey) ? (cfg.x_format ?? cfg.format) : cfg.format;
                    return [formatValue(v, fmt), n];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Scatter
                  name={widget.title ?? "Points"}
                  data={points}
                  fill={cfg.color ?? "#3b82f6"}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
